import {ES, minuteInMS, Observer, secondInMS, TextPlus} from "web-vcore";
import React, {useState} from "react";
import {CheckBox, Column, Row, RowLR, Select, Spinner, Text, TextArea, TextInput, TimeSpanInput} from "react-vcomponents";
import {BaseComponent} from "react-vextensions";
import {GetLights_WithUserTag} from "../../../../Server/DB/lights.js";
import {ControlInput} from "../../../@Shared/ControlInput.js";
import {EffectPreviewButton} from "../../../@Shared/TagTargetEntryUI.js";
import {E, GetEntries, ToNumber} from "js-vextensions";
import {EngineConfigPanels_SharedProps, ExtendEngineConfigPanelSharedProps} from "../ConfigPanel_Local.js";
import {observer_mgl} from "mobx-graphlink";
import {SpinnerForPercent} from "../../../../Utils/ReactComponents/SpinnerForPercent.js";
import {ThinkingType, EffortLevel} from "../../../../Server/DB/engineConfigs/@EngineConfig/@EC_StoryExplore.js";

const GetSharedProps = (props: EngineConfigPanels_SharedProps)=>ExtendEngineConfigPanelSharedProps(props, c=>c.storyExplore, (c, v)=>c.storyExplore = v);

const splitAt = 150;

export const StoryExplore_ConfigUI = observer_mgl((props: {remote?: boolean} & EngineConfigPanels_SharedProps)=>{
	const {enabled} = props;
	const sharedProps = GetSharedProps(props);
	const {config, ChangeConfig} = sharedProps;

	const [showStartPrompt, setShowStartPrompt] = useState(false);

	return (
		<Column mt={15} style={{background: "hsla(0,0%,100%,.1)", borderRadius: 5, padding: 5}}>
			<Row style={{fontSize: 18}}>
				<CheckBox text="Story Explore" enabled={enabled} value={config.enabled} onChange={val=>ChangeConfig(c=>c.enabled = val)}/>
			</Row>

			{config.enabled &&
			<>
				<RowLR mt={5} splitAt={splitAt}>
					<Text>Previous text:</Text>
					<ControlInput enabled={enabled} value={config.prevText_triggerSet} onChange={val=>ChangeConfig(c=>c.prevText_triggerSet = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<Text>Next text:</Text>
					<ControlInput enabled={enabled} value={config.nextText_triggerSet} onChange={val=>ChangeConfig(c=>c.nextText_triggerSet = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<Text>Select text:</Text>
					<ControlInput enabled={enabled} value={config.selectText_triggerSet} onChange={val=>ChangeConfig(c=>c.selectText_triggerSet = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<Text>Stop voice:</Text>
					<ControlInput enabled={enabled} value={config.stopVoice_triggerSet} onChange={val=>ChangeConfig(c=>c.stopVoice_triggerSet = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<Text>New story:</Text>
					<ControlInput enabled={enabled} value={config.newStory_triggerSet} onChange={val=>ChangeConfig(c=>c.newStory_triggerSet = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<Text>Voice, tag:</Text>
					<TextInput enabled={enabled} style={{width: 80}} value={config.voiceSoundTag} onChange={val=>ChangeConfig(c=>c.voiceSoundTag = val)}/>
					<Text ml={5} mr={5}>Vol. mult.:</Text>
					<SpinnerForPercent enabled={enabled} min={0} max={999} value={config.volumeMultiplier} onChange={val=>ChangeConfig(c=>c.volumeMultiplier = val)}/>
				</RowLR>
				<RowLR mt={5}>
					<TextPlus info={`If LLM produces a response with fewer than this many numbered items, selecting a non-numbered line will tell the LLM to generate a full list.`.AsMultiline(0)}>Min numbered-list size:</TextPlus>
					<Spinner min={0} enabled={enabled} value={config.minNumberedListSize} onChange={val=>ChangeConfig(c=>c.minNumberedListSize = val)}/>
				</RowLR>

				<Row mt={10} style={{fontWeight: "bold"}}>LLM history</Row>
				<RowLR mt={5}>
					<TextPlus info={`When sending the message history to the LLM, trim all numbered-lists from prior outputs, except the last X such lists.`.AsMultiline(0)}>Keep last X numbered lists:</TextPlus>
					<Spinner min={1} enabled={enabled} value={config.llmHistory_keepLastXNumberedLists} onChange={val=>ChangeConfig(c=>c.llmHistory_keepLastXNumberedLists = val)}/>
				</RowLR>
				<RowLR mt={5}>
					<TextPlus info={`
						Effects when set:
						* Filters previous messages (when sending to LLM), so that a given message has the last numbered-list, AND user just selected an entry from its "dynamic" list-items, omit all the other dynamic-list options. (thus helping avoid the LLM repeating the same dynamic options; the instructions already tell it to avoid this, but it fairly often repeats them anyway)
					`.AsMultiline(0)}>Dynamic list-numbers:</TextPlus>
					<TextInput style={{flex: 1}} value={(config.dynamicListNumbers || []).join(", ")} onChange={val=>{
						ChangeConfig(c=>c.dynamicListNumbers = val.split(",").map(a=>ToNumber(a.trim(), undefined)).filter(a=>a));
					}}/>
				</RowLR>
				<RowLR mt={5}>
					<TextPlus info={`The higher it is, the more creative/chaotic it is. Range: 0-1. (Ignored when thinking is enabled, as the API requires temperature=1 in that case.)`}>LLM temperature:</TextPlus>
					<Spinner min={0} max={1} style={{flex: 1}} value={config.temperature} onChange={val=>ChangeConfig(c=>c.temperature = val)}/>
				</RowLR>
				<RowLR mt={5}>
					<TextPlus info={`Controls how much compute effort Claude expends on the response. Higher effort generally means better quality but uses more tokens/time.`}>Effort:</TextPlus>
					<Select options={GetEntries(EffortLevel, "ui")} enabled={enabled} value={config.effort} onChange={val=>ChangeConfig(c=>c.effort = val)}/>
				</RowLR>
				<RowLR mt={5}>
					<TextPlus info={`
						"extended": always thinks up to budget_tokens.
						"adaptive": Claude decides whether/how much to think based on query complexity.
						Both require Claude 3.7+; temperature is forced to 1.
					`.AsMultiline(0)}>Thinking type:</TextPlus>
					<Select options={GetEntries(ThinkingType)} enabled={enabled} value={config.thinkingType} onChange={val=>ChangeConfig(c=>c.thinkingType = val)}/>
				</RowLR>
				{config.thinkingType != ThinkingType.disabled && <RowLR mt={5}>
					<TextPlus info={`
						Token limit desired for Claude to use for internal reasoning.
						* In "extended" mode, this limit is fully respected.
						* In "adaptive" thinking mode, we can only specify a total max_tokens; so there, the code just sets max_tokens = thinking tokens + response tokens
						...and lets Claude decide how to allocate it (ie. it may think beyond the thinking-tokens, or respond beyond the response-tokens, but not both)
					`.AsMultiline(0)}>Thinking budget tokens:</TextPlus>
					<Spinner min={1024} enabled={enabled} value={config.thinking_budgetTokens} onChange={val=>ChangeConfig(c=>c.thinking_budgetTokens = val)}/>
				</RowLR>}
				<RowLR mt={5}>
					<TextPlus info={`
						Token limit desired for the visible response.
						* In "thinking disabled" mode, this limit is fully respected.
						* In "extended" mode, this limit is fully respected. (since thinking-tokens limit is fully respected)
						* In "adaptive" thinking mode, we can only specify a total max_tokens; so there, the code just sets max_tokens = thinking tokens + response tokens
						...and lets Claude decide how to allocate it (ie. it may think beyond the thinking-tokens, or respond beyond the response-tokens, but not both)
					`.AsMultiline(0)}>Response tokens:</TextPlus>
					<Spinner min={256} enabled={enabled} value={config.responseTokens} onChange={val=>ChangeConfig(c=>c.responseTokens = val)}/>
				</RowLR>
				<Column mt={5}>
					<Row>
						<TextPlus style={{width: splitAt}} info={`
							* This text (other than the user-prompt subsection) is sent as the LLM's "system prompt" (combined with the story's own system prompt, if set).
							* You can customize the initial user-prompt by wrapping it like so: <userPrompt>This is my user prompt!</userPrompt>
							* System vs user prompt? Apparently, LLM's are more likely to follow system-prompt instructions. (at least as the message-history gets longer)
						`.AsMultiline(0)}>Start prompt:</TextPlus>
						<CheckBox text="Show" value={showStartPrompt} onChange={val=>setShowStartPrompt(val)}/>
					</Row>
					<TextArea enabled={enabled} autoSize={true} style={ES(!showStartPrompt && {display: "none"})} value={config.startPrompt} onChange={val=>ChangeConfig(c=>c.startPrompt = val)}/>
				</Column>
			</>}
		</Column>
	);
});